import { debounce } from 'lodash';

import mixpanel from '@aqx-core/common/mixpanel';
import { closeOrderHelper, eventNameHelper, placeOrderHelper } from '@aqx-core/common/services/mixpanel/helpers';
import { mapFillPolicyIntToText } from '@aqx-core/common/utils/trade-form';

import { EMixpanelEventNames, EMixpanelProperties, EToggle, MIXPANEL_DEBOUNCE_TIME } from '../types';
import {
  IMixpanelBulkCloseOpenPendingClickProps,
  IMixpanelBulkClosePositionsClickProps,
  IMixpanelBulkClosePositionsSubmittedProps,
  IMixpanelCloseOrderSubmitProps,
  IMixpanelConfirmEditOrderClickProps,
  IMixpanelDeleteOrderSubmitProps,
  IMixpanelEditOrderClickProps,
  IMixpanelExpirySelectProps,
  IMixpanelFillPolicySelectProps,
  IMixpanelIncreaseDecreaseProps,
  IMixpanelOrderFieldClickProps,
  IMixpanelOrderTypeSelectProps,
  IMixpanelPlaceOrderDetailsButtonClickProps,
  IMixpanelPlaceTradeClickProps,
  IMixpanelTrackOrderClickProps,
  IMixpanelTradeClickProps,
  ITrackOrderProps
} from './type';

class OrderAnalysisService {
  public OCTEnableDisableClick = (enabled: boolean) => {
    mixpanel.track(EMixpanelEventNames.OCT_TOGGLE_CLICKED, {
      [EMixpanelProperties.TOGGLE]: enabled ? EToggle.ON : EToggle.OFF
    });
  };

  public OCTAgreementClick = () => {
    mixpanel.track(EMixpanelEventNames.OCT_AGREEMENT_CLICKED);
  };

  public tradeClick = ({ symbol, direction }: IMixpanelTradeClickProps) => {
    const placeOrderProperties = placeOrderHelper.createPlaceOrderClickProperties({ symbol, direction });
    mixpanel.track(EMixpanelEventNames.TRADE_CLICKED, {
      ...placeOrderProperties
    });
  };

  public placeTradeOrderClick = ({ symbol, direction, orderType }: IMixpanelPlaceTradeClickProps) => {
    const placeOrderProperties = placeOrderHelper.createPlaceOrderClickProperties({
      symbol,
      direction,
      orderType
    });
    mixpanel.track(EMixpanelEventNames.PLACE_TRADE_ORDER_CLICKED, {
      ...placeOrderProperties
    });
  };

  public tradeOrderSubmit = ({ values, symbolDetails, buySellType, displayTime }: ITrackOrderProps) => {
    const placeOrderSubmittedProperties = placeOrderHelper.createPlaceOrderSubmittedProperties({
      values,
      symbolDetails,
      buySellType,
      displayTime
    });
    mixpanel.track(EMixpanelEventNames.TRADE_ORDER_SUBMITTED, {
      ...placeOrderSubmittedProperties
    });
  };

  public closeOrderClick = ({ symbol, direction }: IMixpanelTradeClickProps) => {
    const placeOrderProperties = placeOrderHelper.createPlaceOrderClickProperties({ symbol, direction });
    mixpanel.track(EMixpanelEventNames.CLOSE_ORDER_CLICKED, {
      ...placeOrderProperties
    });
  };

  public closeOrderSubmit = ({ values, order, livePrice }: IMixpanelCloseOrderSubmitProps) => {
    const CloseOrderSubmittedProperties = closeOrderHelper.createCloseOrderSubmittedProperties({
      values,
      order,
      livePrice
    });
    mixpanel.track(EMixpanelEventNames.CLOSE_ORDER_SUBMITTED, {
      ...CloseOrderSubmittedProperties
    });
  };

  public bulkCloseClick = ({ symbol, numOfPositions }: IMixpanelBulkCloseOpenPendingClickProps) => {
    const bulkCloseOpenPendingProperties = closeOrderHelper.createBulkCloseOpenPendingProperties({
      symbol,
      numOfPositions
    });
    mixpanel.track(EMixpanelEventNames.BULK_CLOSE_CLICKED, {
      ...bulkCloseOpenPendingProperties
    });
  };

  public bulkClosePositionsClick = ({
    symbol,
    numOfPositions,
    bulkCloseType
  }: IMixpanelBulkClosePositionsClickProps) => {
    const bulkCloseOpenPendingProperties = closeOrderHelper.createBulkCloseOpenPendingProperties({
      symbol,
      numOfPositions,
      bulkCloseType
    });
    mixpanel.track(EMixpanelEventNames.BULK_CLOSE_POSITIONS_CLICKED, {
      ...bulkCloseOpenPendingProperties
    });
  };

  public bulkClosePositionsSubmit = ({
    symbolDetails,
    numOfPositions,
    bulkCloseType
  }: IMixpanelBulkClosePositionsSubmittedProps) => {
    const bulkCloseOpenPendingProperties = closeOrderHelper.createBulkCloseOpenPendingSubmittedProperties({
      symbolDetails,
      numOfPositions,
      bulkCloseType
    });
    mixpanel.track(EMixpanelEventNames.BULK_CLOSE_POSITIONS_SUBMITTED, {
      ...bulkCloseOpenPendingProperties
    });
  };

  public deleteOrderClick = ({ symbol, direction }: IMixpanelTradeClickProps) => {
    const placeOrderProperties = placeOrderHelper.createPlaceOrderClickProperties({ symbol, direction });
    mixpanel.track(EMixpanelEventNames.DELETE_ORDER_CLICKED, {
      ...placeOrderProperties
    });
  };

  public deletePendingOrderSubmit = ({ order, livePrice }: IMixpanelDeleteOrderSubmitProps) => {
    const deleteOrderProperties = closeOrderHelper.createDeleteOrderSubmittedProperties({ order, livePrice });
    mixpanel.track(EMixpanelEventNames.DELETE_PENDING_ORDER_SUBMITTIED, {
      ...deleteOrderProperties
    });
  };

  public bulkDeleteClick = ({ symbol, numOfPositions }: IMixpanelBulkCloseOpenPendingClickProps) => {
    const bulkCloseOpenPendingProperties = closeOrderHelper.createBulkCloseOpenPendingProperties({
      symbol,
      numOfPositions
    });
    mixpanel.track(EMixpanelEventNames.BULK_DELETE_CLICKED, {
      ...bulkCloseOpenPendingProperties
    });
  };

  public bulkDeletePendingOrdersSubmit = ({
    symbolDetails,
    numOfPositions
  }: IMixpanelBulkClosePositionsSubmittedProps) => {
    const bulkCloseOpenPendingProperties = closeOrderHelper.createBulkCloseOpenPendingSubmittedProperties({
      symbolDetails,
      numOfPositions
    });
    mixpanel.track(EMixpanelEventNames.BULK_DELETE_PENDING_ORDERS_SUBMITTED, {
      ...bulkCloseOpenPendingProperties
    });
  };

  public orderFieldClick = ({ action, symbol, modalType }: IMixpanelOrderFieldClickProps) => {
    const placeOrderDetailsButtonClickProperties = placeOrderHelper.createPlaceOrderDetailsButtonClickProperties({
      symbol,
      modalType
    });
    const eventName = eventNameHelper.mapOrderFieldClickEventName(action);
    mixpanel.track(eventName, {
      ...placeOrderDetailsButtonClickProperties
    });
  };

  public increaseDecrease = debounce(({ action, symbol, modalType }: IMixpanelIncreaseDecreaseProps) => {
    const placeOrderDetailsButtonClickProperties = placeOrderHelper.createPlaceOrderDetailsButtonClickProperties({
      symbol,
      modalType
    });
    const eventName = eventNameHelper.mapIncreaseDecreaseEventName(action);
    mixpanel.track(eventName, {
      ...placeOrderDetailsButtonClickProperties
    });
  }, MIXPANEL_DEBOUNCE_TIME);

  public orderTypeSelect = ({ orderTypeLabel, symbol, modalType }: IMixpanelOrderTypeSelectProps) => {
    const placeOrderDetailsButtonClickProperties = placeOrderHelper.createPlaceOrderDetailsButtonClickProperties({
      symbol,
      modalType
    });
    mixpanel.track(EMixpanelEventNames.ORDER_TYPE_SELECTED, {
      ...placeOrderDetailsButtonClickProperties,
      [EMixpanelProperties.ORDER_TYPE]: orderTypeLabel ? orderTypeLabel : '--'
    });
  };

  public fillPolicySelect = ({ fillPolicy, symbol, modalType }: IMixpanelFillPolicySelectProps) => {
    const placeOrderDetailsButtonClickProperties = placeOrderHelper.createPlaceOrderDetailsButtonClickProperties({
      symbol,
      modalType
    });
    mixpanel.track(EMixpanelEventNames.FILL_POLICY_SELECTED, {
      ...placeOrderDetailsButtonClickProperties,
      [EMixpanelProperties.FILL_POLICY]: fillPolicy ? mapFillPolicyIntToText(fillPolicy) : '--'
    });
  };

  public expirySelect = ({ expiryType, symbol, modalType }: IMixpanelExpirySelectProps) => {
    const placeOrderDetailsButtonClickProperties = placeOrderHelper.createPlaceOrderDetailsButtonClickProperties({
      symbol,
      modalType
    });
    mixpanel.track(EMixpanelEventNames.EXPIRY_SELECTED, {
      ...placeOrderDetailsButtonClickProperties,
      [EMixpanelProperties.TRADE_EXPIRY]: expiryType
    });
  };

  public expiryDateSelect = ({ symbol, modalType }: IMixpanelPlaceOrderDetailsButtonClickProps) => {
    const placeOrderDetailsButtonClickProperties = placeOrderHelper.createPlaceOrderDetailsButtonClickProperties({
      symbol,
      modalType
    });
    mixpanel.track(EMixpanelEventNames.EXPIRY_DATE_SELECTED, {
      ...placeOrderDetailsButtonClickProperties
    });
  };

  public expiryTimeSelect = ({ symbol, modalType }: IMixpanelPlaceOrderDetailsButtonClickProps) => {
    const placeOrderDetailsButtonClickProperties = placeOrderHelper.createPlaceOrderDetailsButtonClickProperties({
      symbol,
      modalType
    });
    mixpanel.track(EMixpanelEventNames.EXPIRY_TIME_SELECTED, {
      ...placeOrderDetailsButtonClickProperties
    });
  };

  public editOrderClick = ({ symbol, direction, orderType, positionModuleTab }: IMixpanelEditOrderClickProps) => {
    const placeOrderProperties = placeOrderHelper.createPlaceOrderClickProperties({ symbol, direction, orderType });
    mixpanel.track(EMixpanelEventNames.EDIT_ORDER_CLICKED, {
      ...placeOrderProperties,
      [EMixpanelProperties.POSITION_MODULE_TAB]: positionModuleTab
    });
  };

  public confirmEditOrderClick = ({
    positionModuleTab,
    order,
    values,
    livePrice
  }: IMixpanelConfirmEditOrderClickProps) => {
    const placeOrderProperties = placeOrderHelper.createConfirmEditOrderClickProperties({
      positionModuleTab,
      order,
      values,
      livePrice
    });
    mixpanel.track(EMixpanelEventNames.CONFIRM_EDIT_ORDER_CLICKED, {
      ...placeOrderProperties
    });
  };

  public editOrderSubmitted = ({
    positionModuleTab,
    order,
    values,
    livePrice
  }: IMixpanelConfirmEditOrderClickProps) => {
    const placeOrderProperties = placeOrderHelper.createConfirmEditOrderClickProperties({
      positionModuleTab,
      order,
      values,
      livePrice
    });
    mixpanel.track(EMixpanelEventNames.EDIT_ORDER_SUBMITTED, {
      ...placeOrderProperties
    });
  };

  public trackOrderClick = ({
    trackOrderParams,
    livePrice,
    priceTrigger,
    positionModuleTab,
    isTracking
  }: IMixpanelTrackOrderClickProps) => {
    const trackedOrderProperties = placeOrderHelper.createTracKOrderClickProperties({
      trackOrderParams,
      livePrice,
      positionModuleTab,
      isTracking,
      priceTrigger
    });
    mixpanel.track(EMixpanelEventNames.TRACK_ORDER_CLICKED, {
      ...trackedOrderProperties
    });
  };
}

const orderAnalysisService = new OrderAnalysisService();
export default orderAnalysisService;
